import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { useResponsive } from '../utils/responsive';
import { Navbar } from './Navbar.web';
import { TabBar, TabScreen } from './TabBar';
import { Fab } from './Fab';
import { LAYOUT, Brand, Colors, Spacing } from '../constants';

/** Top padding of the white column when a `band` straddles its upper edge. */
const BAND_COLUMN_PADDING_TOP = 76;

interface ScreenLayoutProps {
  children: React.ReactNode;
  showNavbar?: boolean;
  /**
   * If true, content will be full-width (no max-width constraint)
   * Useful for dashboard-style layouts with sidebars
   */
  fullWidth?: boolean;
  /** Width of the centred desktop column (default 1200; 800 for reading pages, 600 for forms) */
  maxWidth?: number;
  /** Background of the desktop column (default white); ignored for `fullWidth` screens, which sit on the linen page */
  backgroundColor?: string;
  /** Desktop: standard page padding inside the column (32 top, 40 sides, 48 bottom) */
  desktopPadding?: boolean;
  /**
   * Desktop only: a full-width Bosgroen band (`PageBand`) between the navbar
   * and the column. When set, the column's top padding grows to 76 so the
   * band's straddling toolbar has room (see `bandToolbar`). Ignored on mobile, where screens
   * render their own white header.
   */
  band?: React.ReactNode;
  /**
   * Whether the `band` carries a straddling toolbar (default true). A band
   * without one closes on the green, so the column keeps its normal padding
   * instead of the 76 that makes room for the toolbar's lower half.
   */
  bandToolbar?: boolean;
  /**
   * Mobile only: which bottom tab this screen belongs to. Renders the
   * TabBar; give scrolling content `LAYOUT.tabBar.contentPadding` bottom
   * padding so it clears the bar.
   */
  mobileTab?: TabScreen;
  /** Mobile only: renders the floating "+" above the TabBar */
  onFabPress?: () => void;
}

/**
 * Page shell (polish round).
 *
 * Desktop: fixed Bosgroen navbar, an optional full-width `band` (`PageBand`)
 * that continues the navbar green, a linen page, and a centred white column
 * with hairline edges. Mobile: edge-to-edge screen with the bottom TabBar
 * and optional Fab underneath the content.
 */
export const ScreenLayout: React.FC<ScreenLayoutProps> = ({
  children,
  showNavbar = true,
  fullWidth = false,
  maxWidth = LAYOUT.maxContentWidth,
  backgroundColor = Colors.white,
  desktopPadding = false,
  band,
  bandToolbar = true,
  mobileTab,
  onFabPress,
}) => {
  const { isDesktop } = useResponsive();
  const shouldShowNavbar = isDesktop && showNavbar;
  const column = isDesktop && !fullWidth;
  const showBand = isDesktop && !!band;

  return (
    <View style={styles.container}>
      {shouldShowNavbar && <Navbar />}
      <View style={[styles.content, shouldShowNavbar && styles.contentWithNavbar]}>
        {showBand ? <View style={styles.band}>{band}</View> : null}
        {/* Centered max-width column for desktop */}
        <View style={[
          styles.contentWrapper,
          column && styles.column,
          column && !showBand && styles.columnFill,
          column && { maxWidth },
          column && desktopPadding && styles.columnPadded,
          column && showBand && bandToolbar && styles.columnUnderBand,
          column && { backgroundColor },
        ]}>
          {children}
        </View>
      </View>
      {!isDesktop && onFabPress ? <Fab onPress={onFabPress} /> : null}
      {!isDesktop && mobileTab ? <TabBar current={mobileTab} /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background, // Linen page ground frames the white column
  },
  content: {
    flex: 1,
  },
  contentWithNavbar: {
    paddingTop: LAYOUT.navbar.desktop, // Height of fixed navbar
  },
  contentWrapper: {
    flex: 1,
    width: '100%',
  },
  column: {
    alignSelf: 'center', // Centers the container
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: Colors.borderLight,
  },
  // Stretch the column to the bottom on short pages. Only without a band: with
  // one, 100% of the content area plus the band's height overshoots the
  // viewport, the app shell clips the overflow, and the bottom of the list
  // inside becomes unreachable — `flex: 1` already fills the space under the band.
  columnFill: {
    ...(Platform.OS === 'web' && { minHeight: '100%' } as any),
  },
  columnPadded: {
    paddingTop: Spacing['2xl'],
    paddingHorizontal: Spacing['3xl'],
    paddingBottom: Spacing['4xl'],
  },
  band: {
    width: '100%',
    backgroundColor: Brand.bosgroen, // continues the navbar green, no gap under it
    // Above the column, so the toolbar's lower half is not painted over.
    zIndex: 2,
  },
  columnUnderBand: {
    // Clears the lower half of the band's straddling toolbar panel.
    paddingTop: BAND_COLUMN_PADDING_TOP,
  },
});
